import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './CuriosityCards.css';
import { fetchTopics, getCachedTopics, prefetchTopicDetail } from '../lib/contentCache';

const CuriosityCards = ({ onReady, suppressLoadingStatus = false }) => {
  const cachedTopics = getCachedTopics();
  const [topics, setTopics] = useState(() => cachedTopics ?? []);
  const [loading, setLoading] = useState(() => !cachedTopics);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const readyNotifiedRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    fetchTopics()
      .then((nextTopics) => {
        if (!isMounted) return;
        setTopics(nextTopics);
        setError(null);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError('无法加载主题信息');
        console.error(err);
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    if (readyNotifiedRef.current) return;
    readyNotifiedRef.current = true;
    if (typeof onReady === 'function') {
      onReady();
    }
  }, [loading, onReady]);

  const handleMouseEnter = useCallback((index) => {
    setActiveIndex(index);
  }, []);

  const handleClick = useCallback((topicId) => {
    prefetchTopicDetail(topicId);
    navigate(`/topics/${topicId}`, {
      state: {
        fromCuriosity: true,
        backgroundLocation: location,
      },
    });
  }, [location, navigate]);

  const visibleTopics = topics.slice(0, 5);
  const centerIndex = Math.floor(visibleTopics.length / 2);

  const getCardStyle = useCallback((index) => {
    const style = {};
    const offset = index - centerIndex;
    const cardWidth = 383.02;
    const initialSpacing = cardWidth * (2 / 3);
    const hoverShift = cardWidth * 0.1;

    const initialX = offset * initialSpacing;

    if (activeIndex !== null) {
      const hoverOffset = index - activeIndex;
      let finalX = initialX;

      if (hoverOffset > 0) {
        finalX += hoverShift;
      } else if (hoverOffset < 0) {
        finalX -= hoverShift;
      }

      const scale = index === activeIndex ? 1.1 : 0.9 - (Math.abs(hoverOffset) * 0.05);
      style.transform = `translateX(${finalX}px) scale(${scale}) translateZ(0)`;
      style.zIndex = index === activeIndex ? 6 : 5 - Math.abs(hoverOffset);
    } else {
      style.transform = `translateX(${initialX}px) scale(${1 - Math.abs(offset) * 0.1}) translateZ(0)`;
      style.zIndex = 5 - Math.abs(offset);
    }

    return style;
  }, [activeIndex, centerIndex]);

  if (loading && suppressLoadingStatus) {
    return (
      <div id="curiosity-section" className="curiosity-container is-placeholder" aria-hidden="true">
        <h2 className="curiosity-heading-placeholder">Explore...</h2>
        <div className="curiosity-cards curiosity-cards--placeholder" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="curiosity-container" role="status" aria-live="polite">
        <h2>正在加载...</h2>
      </div>
    );
  }

  if (error) {
    return <div className="curiosity-container" role="alert"><h2>{error}</h2></div>;
  }

  return (
    <div id="curiosity-section" className="curiosity-container">
      <h2>Explore...</h2>
      <div className="curiosity-cards" onMouseLeave={() => setActiveIndex(null)}>
        {visibleTopics.map((topic, index) => (
          <div
            key={topic.id}
            className="curiosity-card"
            style={getCardStyle(index)}
            onMouseEnter={() => {
              handleMouseEnter(index);
              prefetchTopicDetail(topic.id);
            }}
            onFocus={() => {
              handleMouseEnter(index);
              prefetchTopicDetail(topic.id);
            }}
            onPointerDown={() => {
              prefetchTopicDetail(topic.id);
            }}
            onClick={() => handleClick(topic.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick(topic.id);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`进入主题：${topic.title}`}
          >
            <img src={topic.image_url} alt={topic.title} className="curiosity-card-image" />
            <div
              className={`curiosity-card-title${index > centerIndex ? ' curiosity-card-title--right' : ''}`}
            >
              {topic.title}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CuriosityCards;
